import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import DoramaCard from '../components/DoramaCard';
import {
  useGetListsQuery,
  useGetItemsQuery,
  useGetAllItemsQuery,
} from '../services/bookmarksApi';
import styles from '../css/Bookmarks.module.css';

const SYSTEM_KEY_ORDER = [
  'watching',
  'plan_to_watch',
  'completed',
  'favorites',
  'dropped',
  'on_hold',
];

const SYSTEM_KEY_ICONS = {
  watching: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10" />
      <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
    </svg>
  ),
  plan_to_watch: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  completed: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  favorites: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  dropped: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  ),
  on_hold: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="6" y="4" width="4" height="16" />
      <rect x="14" y="4" width="4" height="16" />
    </svg>
  ),
};

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ── Контент вкладки «Все» ────────────────────────────────────────────────────

const AllContent = ({ totalItems }) => {
  const [page, setPage] = useState(1);
  const perPage = 24;

  const { data, isLoading, isFetching } = useGetAllItemsQuery(
    { page, perPage },
    { refetchOnMountOrArgChange: true }
  );

  const items = data?.data ?? [];
  const totalPages = data?.meta?.total_pages ?? 1;

  if (isLoading) {
    return <div className={styles.contentLoading}>Загрузка...</div>;
  }

  if (totalItems === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          </svg>
        </div>
        <p className={styles.emptyTitle}>Закладок пока нет</p>
        <p className={styles.emptyDescription}>Добавляйте дорамы из карточек или страницы просмотра</p>
      </div>
    );
  }

  return (
    <>
      <div className={`${styles.dramasGrid} ${isFetching ? styles.fading : ''}`}>
        {items.map((item) => {
          const m = item.material;
          if (!m) return null;
          return (
            <div key={item.id} className={styles.cardWrapper}>
              <DoramaCard
                id={item.material_id}
                slug={m.slug}
                slug_id={m.slug_id}
                image={m.poster_url ? `https://doramahype.ru/uploads/posters/${m.poster_url}` : null}
                title={m.title}
                year={null}
                genres={[]}
              />
              <p className={styles.cardDate}>{formatDate(item.added_at)}</p>
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button className={styles.pageButton} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>←</button>
          <span className={styles.pageInfo}>{page} / {totalPages}</span>
          <button className={styles.pageButton} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>→</button>
        </div>
      )}
    </>
  );
};

// ── Контент выбранного списка ────────────────────────────────────────────────

const ListContent = ({ list }) => {
  const [page, setPage] = useState(1);
  const perPage = 24;

  const { data, isLoading, isFetching } = useGetItemsQuery(
    { listId: list.id, page, perPage },
    { refetchOnMountOrArgChange: true }
  );

  const items = data?.data ?? [];
  const totalPages = data?.meta?.total_pages ?? 1;

  if (isLoading) {
    return <div className={styles.contentLoading}>Загрузка...</div>;
  }

  if (items.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          </svg>
        </div>
        <p className={styles.emptyTitle}>Пока пусто</p>
        <p className={styles.emptyDescription}>Добавляйте дорамы из карточек или страницы просмотра</p>
      </div>
    );
  }

  return (
    <>
      <div className={`${styles.dramasGrid} ${isFetching ? styles.fading : ''}`}>
        {items.map((item) => {
          const m = item.material;
          if (!m) return null;
          return (
            <div key={item.id} className={styles.cardWrapper}>
              <DoramaCard
                id={item.material_id}
                slug={m.slug}
                slug_id={m.slug_id}
                image={m.poster_url ? `https://doramahype.ru/uploads/posters/${m.poster_url}` : null}
                title={m.title}
                year={null}
                genres={[]}
              />
              <p className={styles.cardDate}>{formatDate(item.added_at)}</p>
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageButton}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            ←
          </button>
          <span className={styles.pageInfo}>{page} / {totalPages}</span>
          <button
            className={styles.pageButton}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            →
          </button>
        </div>
      )}
    </>
  );
};

// ── Главная страница закладок ────────────────────────────────────────────────

const Bookmarks = () => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [activeKey, setActiveKey] = useState('all');

  const { data, isLoading } = useGetListsQuery(undefined, {
    skip: !isAuthenticated,
    refetchOnMountOrArgChange: true,
  });

  const lists = data?.data ?? [];
  const systemLists = SYSTEM_KEY_ORDER
    .map((key) => lists.find((l) => l.system_key === key))
    .filter(Boolean);
  const totalItems = lists.reduce((sum, l) => sum + (l.item_count ?? 0), 0);

  const activeList = activeKey === 'all' ? null : systemLists.find((l) => l.system_key === activeKey);

  // ── Гость ──
  if (!isAuthenticated) {
    return (
      <div className={styles.bookmarksPage}>
        <div className={styles.guestState}>
          <div className={styles.guestTop}>
            <div className={styles.guestBadge}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
              Закладки
            </div>

            <h1 className={styles.guestTitle}>Сохраняйте дорамы, чтобы не потерять их</h1>
            <p className={styles.guestText}>
              Эта страница собирает ваши сохранённые тайтлы в одном месте. После входа здесь появятся личные списки, и вы сможете быстро возвращаться к интересным дорамам.
            </p>

            <div className={styles.guestActions}>
              <Link to="/auth/login" className={styles.loginButton}>Войти</Link>
              <Link to="/catalog" className={styles.secondaryButton}>Открыть каталог</Link>
            </div>
          </div>

          <div className={styles.guestShelf}>
            <div className={styles.guestShelfHeader}>
              <span className={styles.guestShelfTitle}>Что дадут закладки</span>
              <span className={styles.guestShelfHint}>После входа</span>
            </div>

            <div className={styles.guestListRow}>
              <div className={styles.guestListCard}>
                <span className={`${styles.guestListDot} ${styles.guestListDotWarm}`} />
                <div className={styles.guestListMeta}>
                  <span className={styles.guestListName}>Сохранять интересные дорамы</span>
                  <span className={styles.guestListInfo}>Чтобы не искать их заново позже</span>
                </div>
              </div>
              <div className={styles.guestListCard}>
                <span className={`${styles.guestListDot} ${styles.guestListDotSoft}`} />
                <div className={styles.guestListMeta}>
                  <span className={styles.guestListName}>Быстро возвращаться к сохранённому</span>
                  <span className={styles.guestListInfo}>Все нужные тайтлы будут в одном месте</span>
                </div>
              </div>
              <div className={styles.guestListCard}>
                <span className={`${styles.guestListDot} ${styles.guestListDotCool}`} />
                <div className={styles.guestListMeta}>
                  <span className={styles.guestListName}>Держать порядок в просмотре</span>
                  <span className={styles.guestListInfo}>И не терять то, что хочется посмотреть</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.bookmarksPage}>
        <div className={styles.loadingState}>Загрузка закладок...</div>
      </div>
    );
  }

  return (
    <div className={styles.bookmarksPage}>
      <div className={styles.layout}>

        {/* ── Левая колонка — список ── */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarInner}>
            <p className={styles.sidebarLabel}>Закладки</p>

            {/* Все */}
            <button
              className={`${styles.navItem} ${activeKey === 'all' ? styles.navItemActive : ''}`}
              onClick={() => setActiveKey('all')}
            >
              <span className={styles.navIcon}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
              </span>
              <span className={styles.navName}>Все</span>
              <span className={styles.navCount}>{totalItems}</span>
            </button>

            <div className={styles.navDivider} />

            {/* Системные списки */}
            {systemLists.map((list) => (
              <button
                key={list.system_key}
                className={`${styles.navItem} ${activeKey === list.system_key ? styles.navItemActive : ''}`}
                onClick={() => setActiveKey(list.system_key)}
              >
                <span className={styles.navIcon}>
                  {SYSTEM_KEY_ICONS[list.system_key]}
                </span>
                <span className={styles.navName}>{list.name}</span>
                {list.item_count > 0 && (
                  <span className={styles.navCount}>{list.item_count}</span>
                )}
              </button>
            ))}
          </div>
        </aside>

        {/* ── Правая колонка — контент ── */}
        <main className={styles.content}>
          {activeKey === 'all' ? (
            <div className={styles.allView}>
              <div className={styles.allHeader}>
                <h1 className={styles.allTitle}>Все закладки</h1>
                <span className={styles.allCount}>{totalItems}</span>
              </div>
              <AllContent totalItems={totalItems} />
            </div>
          ) : activeList ? (
            <div className={styles.listView}>
              <div className={styles.listViewHeader}>
                <div className={styles.listViewTitleRow}>
                  {activeList.color && (
                    <span className={styles.listViewDot} style={{ backgroundColor: activeList.color }} />
                  )}
                  <h1 className={styles.listViewTitle}>{activeList.name}</h1>
                </div>
              </div>
              <ListContent list={activeList} />
            </div>
          ) : null}
        </main>

      </div>
    </div>
  );
};

export default Bookmarks;
